import type { MouseEvent, ReactNode } from "react";
import type { TFunction } from "i18next";
import type { CandidateInfo } from "../../client";
import UserAvatar from "../../components/UserAvatar";
import { formatUserName } from "../../utils/format/formatUserName";
import AnimatedVoteCount from "./AnimatedVoteCount";


interface CandidateCardProps {
  candidate: CandidateInfo;
  index: number;
  isLeader: boolean;
  onShowTooltip: (e: MouseEvent, candidate: CandidateInfo) => void;
  onShowHistory: (candidate: CandidateInfo) => void;
  t: TFunction;
  /** Vote CTAs when elections are open (or null when closed). */
  children?: ReactNode;
}

/**
 * One row in the election standings: rank, avatar, name, votes,
 * and optional vote-action children.
 */
function CandidateCard({
  candidate,
  index,
  isLeader,
  onShowTooltip,
  onShowHistory,
  t,
  children,
}: CandidateCardProps) {
  return (
    <div
      className="flex flex-col gap-[10px] rounded-lg bg-white-alpha-70 px-xl py-lg backdrop-blur-[8px]"
      style={{
        border: isLeader ? `2px solid var(--color-gold)` : "1px solid var(--color-white-alpha-40)",
      }}
    >
      {/* Candidate info row */}
      <div className="flex min-w-0 items-center gap-lg">
        <div
          className="w-[24px] shrink-0 text-center [font-family:'Rubik_One',sans-serif] text-[18px]"
          style={{
            color: isLeader ? "var(--color-reward-medal)" : 'var(--color-deep-blue)',
          }}
        >
          {index + 1}
        </div>
        <UserAvatar
          user={candidate}
          size={48}
          alt={formatUserName(candidate).shortName}
          onClick={(e) => onShowTooltip(e, candidate)}
        />
        <div className="min-w-0 flex-1">
          <div
            onClick={(e) => onShowTooltip(e, candidate)}
            className="cursor-pointer overflow-hidden text-ellipsis whitespace-nowrap [font-family:Lato,sans-serif] text-[15px] font-bold text-fullscreen-surface"
          >
            {formatUserName(candidate).displayName}
          </div>
          <span
            onClick={() => {
              if (candidate.vote_history?.length) {
                onShowHistory(candidate);
              }
            }}
            className="[font-family:Lato,sans-serif] text-[13px]"
            style={{
              color: candidate.vote_history?.length ? "var(--color-action-middle)" : "var(--color-text-muted)",
              cursor: candidate.vote_history?.length ? "pointer" : "default",
              textDecoration: candidate.vote_history?.length ? "underline" : "none",
            }}
          >
            {t("Votes")}: <AnimatedVoteCount value={candidate.votes_count} large />
          </span>
        </div>
      </div>

      {children}
    </div>
  );
}

export default CandidateCard;
